import { useTenantConfig } from '@/contexts/TenantConfig';
import { useState, useCallback } from 'react';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface GeocodingFeatureProperties {
  coordinates: Coordinates;
  name: string;
  full_address: string;
}

interface GeocodingFeature {
  features: Array<{
    id: string;
    properties: GeocodingFeatureProperties;
  }>;
}

interface EndpointsResponseTypes {
  geoCodingForward: GeocodingFeature;
}

interface RequestCallbacks<T extends keyof EndpointsResponseTypes> {
  onCompleted?: (data: EndpointsResponseTypes[T]) => void;
  onError?: (error: string) => void;
}

const BASE_URL = 'https://api.mapbox.com';

const endpoints = {
  geoCodingForward: '/search/geocode/v6/forward',
} as const;

const useMapboxSearchApi = <T extends keyof EndpointsResponseTypes>(
  endpoint: T,
  options: Record<string, string | number> = {},
  callbacks: RequestCallbacks<T> = {}
) => {
  const [data, setData] = useState<EndpointsResponseTypes[T] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { mapboxConfig } = useTenantConfig();

  const callApi = useCallback(
    async (additionalOptions: Record<string, string | number> = {}) => {
      try {
        const mapboxToken = mapboxConfig.accessToken;
        if (!mapboxToken) {
          throw new Error('Mapbox token not set');
        }
        setLoading(true);

        const url = `${BASE_URL}${endpoints[endpoint]}?${new URLSearchParams({
          ...options,
          ...additionalOptions,
          access_token: mapboxToken,
        })}`;

        const response = await fetch(url, {
          referrerPolicy: 'origin-when-cross-origin',
        });

        if (!response.ok) {
          const errorMessage = 'Mapbox API did not return 200 OK.';
          setError(errorMessage);
          callbacks.onError?.(errorMessage);
          throw new Error(errorMessage);
        }

        const result = await response.json();
        setData(result);
        callbacks.onCompleted?.(result);
      } catch (e: any) {
        setError(e.message);
        callbacks.onError?.(e.message);
      } finally {
        setLoading(false);
      }
    },
    [mapboxConfig.accessToken, endpoint, options, callbacks]
  );

  const reset = useCallback(() => {
    setData(null);
    setLoading(false);
    setError(null);
  }, []);

  return [callApi, { data, loading, error, reset }] as const;
};

export default useMapboxSearchApi;
